import React, { useCallback, useRef, useState } from "react";
import Flashbar, {
  FlashbarProps,
} from "@amzn/awsui-components-console/flashbar";

export interface NotifyInput {
  type?: FlashbarProps.Type;
  header?: React.ReactNode;
  content?: React.ReactNode;
}

/**
 * Hook that owns the list of Flashbar notifications for a page.
 *
 * Keep a single instance of this hook per page and render its `notifications`
 * through the {@link Notifications} component so every action (Validate,
 * Enable, ...) publishes to one consistent banner.
 *
 * Returns:
 * - `notifications`: the current items to hand to {@link Notifications}.
 * - `notify`: adds a dismissible item and returns its id. Dismissing removes
 *   the item from state, so calling `notify` again always shows a fresh banner.
 * - `clearNotifications`: removes all items.
 */
export function useNotifications() {
  const [notifications, setNotifications] = useState<
    FlashbarProps.MessageDefinition[]
  >([]);
  const idCounter = useRef(0);

  const dismiss = useCallback((id: string) => {
    setNotifications((prev) => prev.filter((item) => item.id !== id));
  }, []);

  const notify = useCallback(
    ({ type = "success", header, content }: NotifyInput) => {
      const id = `notification-${idCounter.current++}`;
      const defaultContent =
        type === "error"
          ? "Resource creation failed"
          : "Resource creation succeeded";
      setNotifications((prev) => [
        ...prev,
        {
          id,
          type,
          header,
          content: content ?? defaultContent,
          statusIconAriaLabel: type,
          dismissible: true,
          dismissLabel: "Dismiss message",
          onDismiss: () => dismiss(id),
        },
      ]);
      return id;
    },
    [dismiss],
  );

  const clearNotifications = useCallback(() => setNotifications([]), []);

  return { notifications, notify, clearNotifications };
}

interface NotificationsProps {
  items: ReadonlyArray<FlashbarProps.MessageDefinition>;
}

/**
 * Presentational Flashbar wrapper. Renders the items produced by
 * {@link useNotifications}. Stacks items when more than one is present.
 */
export function Notifications({ items }: NotificationsProps) {
  if (items.length === 0) return null;
  return <Flashbar items={items} stackItems />;
}
